import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useCallback } from 'react';
import { api } from '../api';
import { ChatAPI, UserAPI } from '../../../../shared/types/api';
import { getPushPlatform, PushNotification, PermissionStatus } from '../platform/push';

/**
 * Hook to fetch user info for chat display
 */
export function useChatUsers() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userIds: string[]): Promise<ChatAPI.GetUsersResponse> => {
      return api.post('/chat/users', { userIds });
    },
    onSuccess: (data) => {
      // Cache individual users
      if (data.data?.users) {
        queryClient.setQueryData(['chat', 'users'], (oldData: Record<string, ChatAPI.ChatUser> | undefined) => ({
          ...oldData,
          ...data.data?.users,
        }));
      }
    },
  });
}

/**
 * Hook to get cached chat users
 */
export function useCachedChatUsers() {
  return useQuery<Record<string, ChatAPI.ChatUser>>({
    queryKey: ['chat', 'users'],
    queryFn: () => ({}),
    staleTime: Infinity,
  });
}

/**
 * Hook to send a message to a conversation
 */
export function useSendMessage() {
  return useMutation({
    mutationFn: async (params: { conversationId: string; content: string }): Promise<ChatAPI.SendMessageResponse> => {
      return api.post('/chat/messages', params);
    },
  });
}

/**
 * Hook to register FCM device token
 */
export function useRegisterDevice() {
  return useMutation({
    mutationFn: async (params: ChatAPI.RegisterDeviceRequest): Promise<ChatAPI.RegisterDeviceResponse> => {
      return api.post('/chat/devices', params);
    },
  });
}

/**
 * Hook to unregister FCM device token
 */
export function useUnregisterDevice() {
  return useMutation({
    mutationFn: async (params: ChatAPI.UnregisterDeviceRequest): Promise<ChatAPI.UnregisterDeviceResponse> => {
      return api.delete('/chat/devices', params);
    },
  });
}

/**
 * Hook to manage push notification registration
 * Uses platform abstraction to work with both web and native apps
 */
export function usePushNotifications(options?: {
  onNotification?: (notification: PushNotification) => void;
}) {
  const registerDevice = useRegisterDevice();
  const pushPlatform = useMemo(() => getPushPlatform(), []);

  useEffect(() => {
    if (!pushPlatform.isSupported()) {
      return;
    }

    const platform = pushPlatform.getPlatformId();

    // Try to get existing token first (if permission already granted)
    pushPlatform.getToken().then((token) => {
      if (token) {
        registerDevice.mutate({
          fcmToken: token,
          platform,
          deviceInfo: navigator.userAgent,
        });
      }
    });

    // Listen for foreground notifications
    const unsubscribe = pushPlatform.onNotification((notification) => {
      console.log('Foreground notification received:', notification);
      
      // Call the optional callback for in-app handling
      options?.onNotification?.(notification);
    });

    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pushPlatform]);

  const requestPermission = useCallback(async () => {
    const token = await pushPlatform.requestPermission();
    if (token) {
      await registerDevice.mutateAsync({
        fcmToken: token,
        platform: pushPlatform.getPlatformId(),
        deviceInfo: navigator.userAgent,
      });
      return true;
    }
    return false;
  }, [pushPlatform, registerDevice]);

  const getPermissionStatus = useCallback(async (): Promise<PermissionStatus> => {
    return pushPlatform.getPermissionStatus();
  }, [pushPlatform]);

  return {
    requestPermission,
    getPermissionStatus,
    isSupported: pushPlatform.isSupported(),
  };
}

/**
 * Fetch table info for a table chat (restaurant name, Google Maps link, attendees).
 * Only returns data when the user is assigned to that table.
 */
export function useChatTableInfo(tableId: string | undefined) {
  return useQuery({
    queryKey: ['chat', 'table', tableId],
    queryFn: async (): Promise<UserAPI.ChatTableInfoData> => {
      if (!tableId) throw new Error('No tableId');
      const res = await api.get<UserAPI.GetChatTableInfoResponse>(`/user/chat/table/${tableId}`);
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to fetch table info');
      return res.data as unknown as UserAPI.ChatTableInfoData;
    },
    enabled: !!tableId,
  });
}

